/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { NOT_APPLICABLE } from '@config/constants/common';
import { useValidImage } from '@hooks/useValidImage';
import {
	Avatar,
	Box,
	Card,
	Flex,
	Group,
	Image,
	NumberFormatter,
	Stack,
	Tooltip,
} from '@mantine/core';
import { IconQuestionMark } from '@tabler/icons-react';
import { truncateString } from '@utils/functions';
import { getUnitLabel } from '@utils/units';
import Decimal from 'decimal.js';
import {
	ChangeEventHandler,
	MouseEventHandler,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../Badge/Badge';
import { IconProjectLocation } from '../IconProjectLocation/IconProjectLocation';
import { NumberInput } from '../NumberInput';
import { StatusSwitch } from '../StatusSwitch';
import { Text } from '../Text';
import { Title } from '../Title';
import classes from './MeasurabilityOffsetProjectCard.module.css';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

export type TProjectCardProps = {
	type: 'measurability' | 'additionality';
	projectType: string | undefined;
	name: string;
	cover: string | undefined;
	location: {
		countryCode: string;
		place: string;
		countryName: string;
	};
	availableStock: number;
	active: boolean;
	onStatusChange?: ChangeEventHandler<HTMLInputElement>;
	fullWidth?: boolean;
	supportedByLogo?: string;
	sellPrice: number;
	onClick?: MouseEventHandler<HTMLDivElement>;
	showStatus: boolean;
	readonly: boolean;
	quantity?: number;
	onChangeQuantity?: (value: number) => void;
	isAdded: boolean;
	disabled?: boolean;
	disabledLabel: string;
	disableNumberInput?: boolean;
	remainingQuantity: number;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const MeasurabilityOffsetProjectCard: React.FC<
	TProjectCardProps
> = ({
	name,
	availableStock,
	active,
	onStatusChange,
	fullWidth,
	cover,
	supportedByLogo,
	location,
	projectType,
	sellPrice,
	onClick,
	showStatus = true,
	readonly,
	quantity,
	onChangeQuantity,
	isAdded,
	disabled = false,
	disabledLabel,
	disableNumberInput,
	remainingQuantity,
}) => {
	const { t } = useTranslation();
	const coverUrl = useValidImage(cover);

	const textColorPrimary = disabled
		? '#6C6C6C'
		: 'var(--mantine-color-igluTextPrimary-9)';
	const textColorSecondary = disabled
		? '#6C6C6C'
		: 'var(--mantine-color-igluSecondary-9)';

	const cardBgColor = disabled ? '#f4f3f3ff' : undefined;

	return (
		<Tooltip disabled={!disabled} label={disabledLabel}>
			<Box
				style={{
					cursor: 'not-allowed',
					width: fullWidth ? '100%' : undefined,
				}}
			>
				<Card
					shadow="sm"
					pl="var(--mantine-spacing-xxs)"
					pr="var(--mantine-spacing-xxs)"
					pt="var(--mantine-spacing-xxs)"
					pb="var(--mantine-spacing-sm)"
					radius="var(--mantine-radius-lg)"
					withBorder
					miw="300px"
					maw={fullWidth ? undefined : '300px'}
					flex={fullWidth ? 1 : 0}
					className={`${classes.container} ${disabled ? classes.disabled : ''}`}
					onClick={!disabled ? onClick : undefined}
					style={{
						cursor: disabled ? 'not-allowed' : 'pointer',
						backgroundColor: cardBgColor,
					}}
				>
					<Box
						h={203}
						p="var(--mantine-spacing-xs)"
						style={{
							borderRadius: '24px',
							position: 'relative',
							background: `lightgray url(${coverUrl}) 50% / cover no-repeat`,
						}}
					>
						<Flex
							justify={
								supportedByLogo
									? 'space-between'
									: 'flex-end'
							}
						>
							{supportedByLogo && (
								<Image
									radius="xl"
									src={supportedByLogo}
									h={48}
									w={48}
								/>
							)}
							<Box onClick={(e) => e.stopPropagation()}>
								{showStatus && (
									<StatusSwitch
										inActiveLabel={t('global.inactive')}
										activeLabel={t('global.active')}
										active={active}
										readonly={readonly || disabled}
										onChange={
											!disabled
												? onStatusChange
												: undefined
										}
									/>
								)}
							</Box>
						</Flex>
					</Box>

					<Flex
						direction="column"
						mt="var(--mantine-spacing-sm)"
						px="xxs"
					>
						<Flex gap="xs" mih={20}>
							<Group justify="center" gap={5}>
								<Avatar
									radius="xl"
									src={`https://flagcdn.com/w40/${location?.countryCode.toLowerCase()}.png`}
									size={18}
								>
									<IconQuestionMark size={18} />
								</Avatar>

								<Tooltip
									label={location.countryName}
									disabled={
										disabled ||
										location.countryName.length <= 11
									}
								>
									<Box>
										<Text
											variant="caption"
											c={textColorSecondary}
										>
											{truncateString(
												location.countryName,
												12
											)}
										</Text>
									</Box>
								</Tooltip>
							</Group>

							{location?.place !== NOT_APPLICABLE && (
								<Group gap={5}>
									<IconProjectLocation color="#617EEF" />
									<Tooltip
										label={location.place}
										disabled={
											disabled ||
											location.place.length <= 11
										}
									>
										<Box>
											<Text
												variant="caption"
												c={textColorSecondary}
											>
												{truncateString(
													location.place,
													12
												)}
											</Text>
										</Box>
									</Tooltip>
								</Group>
							)}
						</Flex>

						<Flex mt="xs" gap="xs">
							<Text
								variant="caption"
								c={textColorSecondary}
							>
								{t('placeholders.projectName')}
							</Text>
						</Flex>

						<Tooltip
							label={name}
							disabled={
								disabled || !name || name.length <= 29
							}
						>
							<Box>
								<Text
									variant="subtitle1"
									c={textColorPrimary}
								>
									{name
										? truncateString(name, 30)
										: NOT_APPLICABLE}
								</Text>
							</Box>
						</Tooltip>

						<Flex mt="sm" gap="xs">
							<Text
								variant="caption"
								c={textColorSecondary}
							>
								{t('global.projectType')}
							</Text>

							<Tooltip
								label={projectType && t(projectType)}
								disabled={
									disabled ||
									!projectType ||
									t(projectType).length <= 23
								}
							>
								<Box>
									<Text
										data-testid="project-type"
										variant="body2"
										c={textColorPrimary}
									>
										{projectType
											? truncateString(t(projectType), 24)
											: NOT_APPLICABLE}
									</Text>
								</Box>
							</Tooltip>
						</Flex>

						<Flex mt="xs" gap="xs" align={'center'}>
							<Text
								variant="caption"
								c={textColorSecondary}
							>
								{t('global.availableStock')}
							</Text>

							<Badge
								type={
									availableStock > 0 ? 'SUCCESS' : 'ERROR'
								}
							>
								<Text variant="button">
									<NumberFormatter
										thousandSeparator=" "
										decimalSeparator=","
										value={availableStock}
										decimalScale={2}
									/>
									<Text size="11px" span>
										{` ${getUnitLabel('tco2e')}`}
									</Text>
								</Text>
							</Badge>
						</Flex>

						<Flex mt="sm" gap="xs" align={'center'}>
							<Text
								variant="caption"
								c={textColorSecondary}
							>
								{t('global.sellPrice')}
							</Text>
							<Tooltip
								arrowSize={8}
								withArrow
								label={
									<Stack gap={'5px'} w={'150px'}>
										<Flex justify={'space-between'}>
											<Text
												span
												variant={'caption'}
												c={'white'}
											>
												{t('global.partInuk')}
											</Text>
											<NumberFormatter
												thousandSeparator=" "
												decimalSeparator=","
												value={sellPrice * 0.2}
												decimalScale={2}
												suffix="€"
											/>
										</Flex>
										<Flex justify={'space-between'}>
											<Text
												span
												variant={'caption'}
												c={'white'}
											>
												{t('global.partProject')}
											</Text>
											<NumberFormatter
												thousandSeparator=" "
												decimalSeparator=","
												value={sellPrice * 0.4}
												decimalScale={2}
												suffix="€"
											/>
										</Flex>
										<Flex justify={'space-between'}>
											<Text
												span
												variant={'caption'}
												c={'white'}
											>
												{t('global.partAdditionality')}
											</Text>
											<NumberFormatter
												thousandSeparator=" "
												decimalSeparator=","
												value={sellPrice * 0.4}
												decimalScale={2}
												suffix="€"
											/>
										</Flex>
									</Stack>
								}
							>
								<Box>
									<Title order={6} c={textColorPrimary}>
										<NumberFormatter
											thousandSeparator=" "
											decimalSeparator=","
											value={sellPrice}
											decimalScale={2}
										/>
										<Text
											span
											variant="caption"
											c={textColorPrimary}
										>
											{` ${t('global.sellPriceUnit')}`}
										</Text>
									</Title>
								</Box>
							</Tooltip>
						</Flex>

						<NumberInput
							min={0}
							clampBehavior="strict"
							max={availableStock}
							label={`${t('global.quantityToOffset')} (tCO2e)`}
							mt="24px"
							size="md"
							placeholder="0"
							unit="tCO2e"
							disabled={disableNumberInput || disabled}
							value={quantity}
							defaultValue={0}
							onChange={
								!disabled ? onChangeQuantity : () => {}
							}
							styles={{
								input: {
									border: isAdded
										? '1.5px solid var(--Colors-Blue, #007AFF)'
										: '',
									backgroundColor: disabled
										? '#F0F0F0'
										: undefined,
									color: disabled ? '#6C6C6C' : undefined,
									cursor: disabled
										? 'not-allowed'
										: 'text',
								},
							}}
						/>

						{!!quantity && quantity > 0 && (
							<Flex justify="center" mt="8px">
								<Text
									variant="subtitle1"
									c={disabled ? '#6C6C6C' : '#97AAC1'}
								>
									{`${t('global.remainingToAllocate')} : `}
									<Text
										span
										variant="subtitle1"
										c={disabled ? '#6C6C6C' : '#007AFF'}
									>
										{`${new Decimal(remainingQuantity).toDecimalPlaces(2).toString()} tCO2e`}
									</Text>
								</Text>
							</Flex>
						)}
					</Flex>
				</Card>
			</Box>
		</Tooltip>
	);
};
